import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateChild, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import { DataShareService } from 'services/data-share.service';
import { AccountMembership } from 'app/API.service.custom';
import { accessKeyFromSnapshot, decideAccess } from './route-access-policy';

/**
 * Nav-time route access boundary. Declared as canActivateChild on the app shell
 * route so it covers every (incl. lazy) child. Routes opt in via
 * `data: { access: '<key>' }`; unkeyed routes pass straight through.
 *
 * Phase A / option b: PERMISSIVE when the active account is not yet loaded
 * (cold deep-link) — RouteAccessEnforcer re-checks once the account resolves.
 * The follow-on account-bootstrap-service phase flips this to authoritative.
 */
@Injectable({ providedIn: 'root' })
export class AccessGuard implements CanActivateChild {
  constructor(
    private dataService: DataShareService,
    private router: Router,
    private toastr: ToastrService,
  ) {}

  async canActivateChild(
    _childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Promise<boolean | UrlTree> {
    const key = accessKeyFromSnapshot(state);
    if (!key) return true; // no policy on this route — skip the account read entirely

    // BehaviorSubject: take(1) resolves immediately with the current value
    // (null on a cold deep-link, in which case decideAccess is permissive).
    const account = (await firstValueFrom(
      this.dataService.getActiveAccount().pipe(take(1)),
    )) as AccountMembership;

    const decision = decideAccess(key, account);
    if (decision.allowed) return true;

    if (decision.message) this.toastr.error(decision.message);
    return this.router.parseUrl(decision.redirectTo || '/home/dashboard');
  }
}
